import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAppContext } from "../../AppContext";
import { MyDomain } from "../../App";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { useTranslation } from "react-i18next";

const C = {
  primary: "#1A1A2E",
  accent: "#C9A84C",
  accentLight: "#FDF6E3",
  accentMuted: "rgba(201,168,76,0.15)",
  success: "#10b981",
  border: "#e5e7eb",
  textMuted: "#6b7280",
  textLight: "#9ca3af",
  disabled: "#d1d5db",
};

function MPHomeLoginWithPhoneNumber({ onLoginSuccess }) {
  const { t } = useTranslation();
  const { setUser, User } = useAppContext();
  const [authStep, setAuthStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("se");
  const [callingCode, setCallingCode] = useState("+46");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("user");
    if (saved) {
      const userData = JSON.parse(saved);
      setUser(userData);
      if (onLoginSuccess) onLoginSuccess();
    }
  }, []);

  const sendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 7) {
      setMessage(t("PleaseEnterValidPhone"));
      return;
    }
    setMessage("");
    setLoading(true);
    try {
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") cleanedPhone = cleanedPhone.slice(1);
      const fullPhoneNumber = callingCode + cleanedPhone;
      const response = await axios.post(`${MyDomain}Otp/send`, {
        Phone: fullPhoneNumber,
      });
      if (response.data.success) {
        setMessage(t("OTPSentTo") + " " + fullPhoneNumber);
        setAuthStep(2);
      } else {
        throw new Error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      let errorMessage = t("ErrorMessage");
      if (error.response?.data?.message)
        errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length < 6) {
      setMessage(t("PleaseEnterValidOTP"));
      return;
    }
    setLoading(true);
    try {
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") cleanedPhone = cleanedPhone.slice(1);
      const fullPhoneNumber = callingCode + cleanedPhone;
      const response = await axios.post(`${MyDomain}Otp/verify`, {
        Phone: fullPhoneNumber,
        Otp: otp,
      });
      if (response.data.verified) {
        setMessage(t("PhoneVerifiedSuccessfully"));
        setAuthStep(3);
      } else {
        throw new Error("Invalid verification code");
      }
    } catch (err) {
      setMessage(err.response?.data?.message || t("InvalidOTP"));
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    setOtp("");
    setMessage("");
    await sendOTP();
  };

  const saveUserToBackend = async () => {
    if (!userName.trim()) {
      setMessage(t("PleaseEnterYourName"));
      return;
    }
    setLoading(true);
    try {
      const stripped = phoneNumber.replace(/^0+/, "");
      const phoneWithZero = "0" + stripped;
      const data = {
        CountryNumber: callingCode,
        PhoneNumber: phoneWithZero,
        Name: userName,
      };
      const response = await axios.post(
        `${MyDomain}Users2/UserLoginWithPhoneNumber`,
        data,
        { headers: { "Content-Type": "application/json" } },
      );
      localStorage.setItem("user", JSON.stringify(response.data));
      setUser(response.data);
      setMessage(t("LoginSuccessful"));
      if (onLoginSuccess) setTimeout(() => onLoginSuccess(), 500);
    } catch (err) {
      setMessage(
        t("FailedToSaveUser") + " " + (err.response?.data || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhoneEntry = () => {
    setAuthStep(1);
    setOtp("");
    setMessage("");
  };

  const isError = (msg) =>
    msg.includes(t("ErrorMessage")) ||
    msg.includes(t("InvalidOTP")) ||
    msg.includes(t("FailedToSaveUser")) ||
    msg.includes("Failed") ||
    msg.includes("Invalid");

  const StepDot = ({ num, targetStep }) => {
    const active = authStep === targetStep;
    const done = authStep > targetStep;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: done ? C.success : active ? C.accent : C.border,
            color: done || active ? C.primary : C.textLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "700",
            fontSize: "14px",
            transition: "all 0.3s ease",
          }}
        >
          {done ? "✓" : num}
        </div>
        <span
          style={{
            fontSize: "14px",
            fontWeight: active ? "700" : "500",
            color: done ? C.success : active ? C.accent : C.textLight,
            transition: "color 0.3s ease",
          }}
        >
          {num === 1 ? t("Phone") : num === 2 ? t("Verify") : t("Name")}
        </span>
      </div>
    );
  };

  const Connector = ({ passed }) => (
    <div
      style={{
        width: "30px",
        height: "2px",
        backgroundColor: passed ? C.success : C.border,
        transition: "background-color 0.3s ease",
      }}
    />
  );

  const MessageBox = ({ msg }) =>
    msg ? (
      <div
        style={{
          padding: "12px 16px",
          borderRadius: "10px",
          backgroundColor: isError(msg) ? "#fee2e2" : "#d1fae5",
          color: isError(msg) ? "#991b1b" : "#065f46",
          fontSize: "14px",
          textAlign: "center",
          fontWeight: "500",
        }}
      >
        {msg}
      </div>
    ) : null;

  const PrimaryBtn = ({ onClick, disabled, label }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "16px",
        fontSize: "16px",
        fontWeight: "700",
        border: "none",
        borderRadius: "12px",
        backgroundColor: disabled ? C.disabled : C.primary,
        color: disabled ? "#fff" : C.accent,
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled ? "none" : `0 4px 16px rgba(26,26,46,0.35)`,
        transition: "all 0.3s ease",
        outline: "none",
        letterSpacing: "0.3px",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = `0 6px 20px rgba(26,26,46,0.45)`;
          e.currentTarget.style.backgroundColor = "#16213E";
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = `0 4px 16px rgba(26,26,46,0.35)`;
          e.currentTarget.style.backgroundColor = C.primary;
        }
      }}
    >
      {label}
    </button>
  );

  const SecondaryBtn = ({ onClick, disabled, label }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "14px",
        fontSize: "14px",
        fontWeight: "600",
        border: `1.5px solid ${C.accent}`,
        borderRadius: "12px",
        backgroundColor: "transparent",
        color: C.accent,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = C.accentMuted;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = "transparent";
        }
      }}
    >
      {label}
    </button>
  );

  const GhostBtn = ({ onClick, label }) => (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "14px",
        fontSize: "14px",
        fontWeight: "600",
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
        color: C.textMuted,
        cursor: "pointer",
        transition: "all 0.3s ease",
        outline: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = C.accent;
        e.currentTarget.style.color = C.accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.color = C.textMuted;
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        position: "relative",
        width: "100%",
        maxWidth: "500px",
        margin: "0 auto",
      }}
    >
      {/* Progress Stepper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          padding: "16px 0",
          flexWrap: "wrap",
        }}
      >
        <StepDot num={1} targetStep={1} />
        <Connector passed={authStep > 1} />
        <StepDot num={2} targetStep={2} />
        <Connector passed={authStep > 2} />
        <StepDot num={3} targetStep={3} />
      </div>

      {/* STEP 1: Enter Phone */}
      {authStep === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: C.primary,
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("Welcome")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: C.textMuted,
                textAlign: "center",
              }}
            >
              {t("EnterPhoneToGetStarted")}
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: C.primary,
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("PhoneNumber")}
            </label>

            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "stretch",
                marginTop: "8px",
              }}
            >
              <div style={{ minWidth: "110px" }}>
                <PhoneInput
                  country={countryCode}
                  value=""
                  onChange={(value, country) => {
                    setCountryCode(country.countryCode);
                    setCallingCode("+" + country.dialCode);
                  }}
                  enableSearch
                  disableDropdown={false}
                  inputStyle={{ display: "none" }}
                  buttonStyle={{
                    width: "100%",
                    height: "52px",
                    borderRadius: "12px",
                    border: `2px solid ${C.border}`,
                    backgroundColor: "#fff",
                  }}
                  containerStyle={{ width: "100%" }}
                />
                <div
                  style={{
                    marginTop: "6px",
                    textAlign: "center",
                    fontWeight: "700",
                    fontSize: "14px",
                    color: C.accent,
                  }}
                >
                  {callingCode}
                </div>
              </div>

              <input
                type="tel"
                placeholder="50-123-4567"
                value={phoneNumber}
                onChange={(e) =>
                  setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))
                }
                onKeyPress={(e) => e.key === "Enter" && sendOTP()}
                style={{
                  flex: 1,
                  height: "52px",
                  fontSize: "16px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  border: `2px solid ${C.border}`,
                  borderRadius: "12px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = C.accent)}
                onBlur={(e) => (e.target.style.borderColor = C.border)}
              />
            </div>

            <p
              style={{
                marginTop: "12px",
                fontSize: "14px",
                color: C.textMuted,
                padding: "12px 14px",
                backgroundColor: C.accentLight,
                borderRadius: "8px",
                border: `1px solid rgba(201,168,76,0.2)`,
              }}
            >
              {t("FullNumber")}{" "}
              <strong style={{ color: C.primary }}>
                {callingCode + phoneNumber}
              </strong>
            </p>
          </div>

          <MessageBox msg={message} />

          <PrimaryBtn
            onClick={sendOTP}
            disabled={loading || !phoneNumber}
            label={loading ? t("Sending") : t("SendVerificationCode")}
          />
        </div>
      )}

      {/* STEP 2: Verify OTP */}
      {authStep === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: C.primary,
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("VerifyYourNumber")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: C.textMuted,
                textAlign: "center",
              }}
            >
              {t("WeSentCodeTo")}{" "}
              <strong style={{ color: C.primary }}>
                {callingCode + phoneNumber}
              </strong>
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: C.primary,
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("VerificationCode")}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder={t("EnterSixDigitCode")}
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
              }
              onKeyPress={(e) => e.key === "Enter" && verifyOTP()}
              maxLength={6}
              style={{
                width: "100%",
                height: "60px",
                fontSize: "24px",
                textAlign: "center",
                letterSpacing: "10px",
                paddingLeft: "16px",
                paddingRight: "16px",
                border: `2px solid ${C.border}`,
                borderRadius: "12px",
                outline: "none",
                transition: "border-color 0.3s ease",
                fontWeight: "700",
                color: C.primary,
              }}
              onFocus={(e) => (e.target.style.borderColor = C.accent)}
              onBlur={(e) => (e.target.style.borderColor = C.border)}
            />
          </div>

          <MessageBox msg={message} />

          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <PrimaryBtn
              onClick={verifyOTP}
              disabled={loading || otp.length < 6}
              label={loading ? t("Verifying") : t("VerifyCode")}
            />
            <SecondaryBtn
              onClick={resendOTP}
              disabled={loading}
              label={loading ? t("Sending") : t("ResendCode") || "Resend Code"}
            />
            <GhostBtn
              onClick={goBackToPhoneEntry}
              label={t("ChangePhoneNumber")}
            />
          </div>
        </div>
      )}

      {/* STEP 3: Enter Name */}
      {authStep === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: C.primary,
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("AlmostThere")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: C.textMuted,
                textAlign: "center",
              }}
            >
              {t("EnterNameToComplete")}
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: C.primary,
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("YourName")}
            </label>
            <input
              type="text"
              placeholder={t("EnterFullName")}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && saveUserToBackend()}
              style={{
                width: "100%",
                height: "52px",
                fontSize: "16px",
                paddingLeft: "16px",
                paddingRight: "16px",
                border: `2px solid ${C.border}`,
                borderRadius: "12px",
                outline: "none",
                transition: "border-color 0.3s ease",
                color: C.primary,
              }}
              onFocus={(e) => (e.target.style.borderColor = C.accent)}
              onBlur={(e) => (e.target.style.borderColor = C.border)}
            />
          </div>

          <MessageBox msg={message} />

          <PrimaryBtn
            onClick={saveUserToBackend}
            disabled={loading || !userName.trim()}
            label={loading ? t("Completing") : t("CompleteRegistration")}
          />
        </div>
      )}
    </div>
  );
}

export default MPHomeLoginWithPhoneNumber;
